import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { registerServiceWorker, unregisterServiceWorker } from '../utils/pwaUtils';

interface User {
  _id: string;
  name: string;
  email: string;
  role: 'user' | 'admin';
  createdAt: string;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

interface DashboardAuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
}

const DashboardAuthContext = createContext<DashboardAuthContextType | undefined>(undefined);

const API_BASE = '/api/v1/auth';

export function DashboardAuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
  });

  const checkAuth = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/me`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data && data.data.role === 'admin') {
          setState({
            user: data.data,
            isAuthenticated: true,
            isLoading: false,
          });
          // Register service worker for authenticated admin
          await registerServiceWorker();
          return;
        }
      }

      setState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
      });
    } catch {
      setState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
      });
    }
  }, []);

  const login = useCallback(async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      setState(prev => ({ ...prev, isLoading: true }));

      const response = await fetch(`${API_BASE}/login`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        setState(prev => ({ ...prev, isLoading: false }));
        return { success: false, error: data.error || 'Invalid credentials' };
      }

      // Fetch user data after successful login
      const userResponse = await fetch(`${API_BASE}/me`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const userData = await userResponse.json();

      if (userData.success && userData.data && userData.data.role === 'admin') {
        setState({
          user: userData.data,
          isAuthenticated: true,
          isLoading: false,
        });
        // Register service worker for authenticated admin
        await registerServiceWorker();
        return { success: true };
      }

      // User is not an admin
      await fetch(`${API_BASE}/logout`, {
        method: 'POST',
        credentials: 'include',
      });

      setState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
      });

      return { success: false, error: 'Access denied. Admin privileges required.' };
    } catch {
      setState(prev => ({ ...prev, isLoading: false }));
      return { success: false, error: 'Network error. Please try again.' };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await fetch(`${API_BASE}/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      // Logout locally even if API call fails
    }

    // Unregister service worker
    await unregisterServiceWorker();

    setState({
      user: null,
      isAuthenticated: false,
      isLoading: false,
    });
  }, []);

  // Check auth on mount
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const value: DashboardAuthContextType = {
    ...state,
    login,
    logout,
    checkAuth,
  };

  return (
    <DashboardAuthContext.Provider value={value}>
      {children}
    </DashboardAuthContext.Provider>
  );
}

export function useDashboardAuth(): DashboardAuthContextType {
  const context = useContext(DashboardAuthContext);
  if (context === undefined) {
    throw new Error('useDashboardAuth must be used within a DashboardAuthProvider');
  }
  return context;
}

// Protected Route component
interface ProtectedRouteProps {
  children: ReactNode;
  fallback?: ReactNode;
}

export function ProtectedRoute({ children, fallback }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading } = useDashboardAuth();

  if (isLoading) {
    return fallback || <div className="dashboard-loading">Loading...</div>;
  }

  if (!isAuthenticated) {
    return null;
  }

  return <>{children}</>;
}
